import React, { useState } from 'react';

const TABS = [
  { id: 'attendance', label: 'Attendance Analysis', icon: '📊' },
  { id: 'interventions', label: 'Client Interventions', icon: '🎯' },
  { id: 'action', label: 'Action Plan', icon: '📋' }
];

const INTERVENTION_PAGES = [
  { id: 'critical', title: 'Critical Risk Clients', subtitle: '>50% No-Show Rate', icon: '🚨', borderColor: 'border-red-700', bgColor: 'bg-red-100', hoverColor: 'hover:bg-red-200' },
  { id: 'high', title: 'High Risk Clients', subtitle: '35-50% No-Show Rate', icon: '🔴', borderColor: 'border-pink-400', bgColor: 'bg-pink-50', hoverColor: 'hover:bg-pink-100' },
  { id: 'moderate', title: 'Moderate Risk Clients', subtitle: '25-35% No-Show Rate', icon: '🟡', borderColor: 'border-yellow-500', bgColor: 'bg-yellow-50', hoverColor: 'hover:bg-yellow-100' },
  { id: 'low', title: 'Low Risk Clients', subtitle: '<25% No-Show Rate', icon: '🟢', borderColor: 'border-green-500', bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' },
  { id: 'warning', title: 'Early Warning Signs', subtitle: 'Risk indicators to watch for', icon: '⚠️', borderColor: 'border-blue-400', bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' }
];

const ACTION_PAGES = [
  { id: 'week', title: 'This Week', subtitle: 'Immediate Actions', icon: '🔥', borderColor: 'border-red-500', bgColor: 'bg-red-50', hoverColor: 'hover:bg-red-100' },
  { id: 'month', title: 'This Month', subtitle: 'System Improvements', icon: '📅', borderColor: 'border-yellow-500', bgColor: 'bg-yellow-50', hoverColor: 'hover:bg-yellow-100' },
  { id: 'ongoing', title: 'Ongoing', subtitle: 'Monitor & Adjust', icon: '🔄', borderColor: 'border-green-500', bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' },
  { id: 'success', title: 'How to Measure Success', subtitle: 'Key metrics and targets', icon: '📊', borderColor: 'border-blue-400', bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' }
];

const NoShowAnalytics = ({ chartData }) => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [subPage, setSubPage] = useState(null);

  if (!chartData) return null;

  const {
    totalClients, totalAppointments, totalNoShows,
    practiceNoShowRate, attendanceRate,
    criticalCount, criticalPct, highRiskCount, highRiskPct, moderateRiskCount, lowRiskCount,
    topNoShowClients
  } = chartData;

  const BackButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className="flex items-center text-gray-600 hover:text-gray-900 mb-5 transition-colors text-sm font-medium"
    >
      <span className="mr-2">←</span>
      Back
    </button>
  );

  const NavCard = ({ item, count, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full text-left border-l-4 ${item.borderColor} ${item.bgColor} ${item.hoverColor} rounded-lg p-5 transition-all flex items-center justify-between group`}
    >
      <div className="flex items-center gap-4">
        <span className="text-2xl">{item.icon}</span>
        <div>
          <h3 className="text-base font-bold text-gray-900">{item.title}</h3>
          <p className="text-sm text-gray-500">{item.subtitle}</p>
          {count !== undefined && <p className="text-xs text-gray-400 mt-0.5">{count} clients</p>}
        </div>
      </div>
      <span className="text-gray-400 group-hover:text-gray-600 text-lg">→</span>
    </button>
  );

  // ===== INTERVENTION SUB-PAGES =====
  const renderInterventionPage = () => {
    switch (subPage) {
      case 'critical':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Critical Risk Clients</h2>
            <p className="text-sm text-gray-500 mb-5">{criticalCount} clients with &gt;50% no-show rate</p>

            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">Intervention Strategies:</p>
                <ol className="list-decimal ml-5 space-y-2">
                  <li><strong>Immediate Outreach:</strong> Schedule an urgent check-in call within 48 hours</li>
                  <li><strong>Treatment Viability Review:</strong> Assess whether current modality, frequency, or format is appropriate</li>
                  <li><strong>Crisis Assessment:</strong> Rule out psychosocial barriers, safety concerns, or decompensation</li>
                  <li><strong>Intensive Attendance Protocol:</strong> Daily reminders, telehealth alternatives, and reduced session length if needed</li>
                  <li><strong>Supervisor Consultation:</strong> Bring case to supervision for additional support and guidance</li>
                </ol>
              </div>
              <div className="bg-red-100 rounded-lg p-4 border-l-4 border-red-500">
                <p className="font-semibold mb-1">Sample Script:</p>
                <p className="italic text-gray-600">
                  "I've been concerned about your attendance and wanted to check in. I know life can get overwhelming, and I want to make sure we're setting you up for success. Can we talk about what's been getting in the way and figure out a plan that actually works for your schedule?"
                </p>
              </div>
            </div>
          </div>
        );
      case 'high':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">High Risk Clients</h2>
            <p className="text-sm text-gray-500 mb-5">{highRiskCount} clients with 35-50% no-show rate</p>

            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">Intervention Strategies:</p>
                <ol className="list-decimal ml-5 space-y-2">
                  <li><strong>Attendance Agreement:</strong> Implement formal attendance agreements with clear expectations</li>
                  <li><strong>Front-Loading:</strong> Schedule 2-3 sessions close together to establish attendance pattern</li>
                  <li><strong>Multi-Modal Reminders:</strong> Text + email + phone reminders (48h, 24h, and 2h before sessions)</li>
                  <li><strong>Session Length Modification:</strong> Offer 30-minute sessions to reduce no-show likelihood</li>
                  <li><strong>Therapeutic Alliance Check:</strong> Assess engagement and motivation in treatment</li>
                </ol>
              </div>
              <div className="bg-pink-50 rounded-lg p-4 border-l-4 border-pink-300">
                <p className="font-semibold mb-1">Sample Script:</p>
                <p className="italic text-gray-600">
                  "I've noticed that we've had some challenges with consistent attendance, which can make progress in therapy more difficult. I'm wondering what obstacles might be making it hard to attend regularly? Let's talk about how we might address those together, because consistent sessions will help us make better progress toward your goals."
                </p>
              </div>
            </div>
          </div>
        );
      case 'moderate':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Moderate Risk Clients</h2>
            <p className="text-sm text-gray-500 mb-5">{moderateRiskCount} clients with 15-30% no-show rate</p>

            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">Intervention Strategies:</p>
                <ol className="list-decimal ml-5 space-y-2">
                  <li><strong>Enhanced Reminders:</strong> Dual-channel reminder system with confirmation requirement</li>
                  <li><strong>Motivational Enhancement:</strong> End-of-session discussion about treatment consistency</li>
                  <li><strong>Barrier Assessment:</strong> Structured evaluation of attendance obstacles (transportation, childcare, etc.)</li>
                  <li><strong>Schedule Optimization:</strong> Offer multiple scheduling options to find optimal time</li>
                  <li><strong>Between-Session Engagement:</strong> Brief check-ins to maintain connection</li>
                </ol>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-300">
                <p className="font-semibold mb-1">Barrier Assessment Questions:</p>
                <ol className="list-decimal ml-5 text-gray-600 space-y-1">
                  <li>What makes it difficult to attend sessions consistently?</li>
                  <li>What time of day works best for your schedule?</li>
                  <li>How helpful are our current appointment reminders?</li>
                  <li>What would make it easier to prioritize therapy appointments?</li>
                  <li>How motivated do you feel about the therapy process right now?</li>
                </ol>
              </div>
            </div>
          </div>
        );
      case 'low':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Low Risk Clients</h2>
            <p className="text-sm text-gray-500 mb-5">{lowRiskCount} clients with &lt;25% no-show rate</p>

            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">Retention Strategies:</p>
                <ol className="list-decimal ml-5 space-y-2">
                  <li><strong>Attendance Reinforcement:</strong> Acknowledge and appreciate consistent attendance</li>
                  <li><strong>Standard Reminders:</strong> Maintain current reminder system</li>
                  <li><strong>Preferred Scheduling:</strong> Offer priority access to preferred time slots</li>
                  <li><strong>Therapeutic Alliance Focus:</strong> Continue strengthening the therapeutic relationship</li>
                  <li><strong>Progress Reviews:</strong> Regular discussions of treatment progress and achievements</li>
                </ol>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-300">
                <p className="font-semibold mb-1">Reinforcement Script:</p>
                <p className="italic text-gray-600">
                  "I appreciate your consistency in attending our sessions. It really shows your commitment to the therapy process and makes a significant difference in what we're able to accomplish together."
                </p>
              </div>
            </div>
          </div>
        );
      case 'warning':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Early Warning Signs</h2>
            <p className="text-sm text-gray-500 mb-5">Indicators that a client may be at risk of increased no-shows</p>

            <div className="text-sm text-gray-700">
              <ul className="space-y-3">
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">📉</span>
                  <div><strong>Pattern Changes:</strong> Previously consistent client begins to reschedule</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">😶</span>
                  <div><strong>Engagement Shifts:</strong> Decreased participation during sessions</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">⏰</span>
                  <div><strong>Punctuality Issues:</strong> Previously punctual client begins arriving late</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">📱</span>
                  <div><strong>Communication Changes:</strong> Delayed responses to reminders</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">😓</span>
                  <div><strong>Life Stressor Comments:</strong> Mentions of increased schedule conflicts</div>
                </li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ===== ACTION PLAN SUB-PAGES =====
  const renderActionPage = () => {
    switch (subPage) {
      case 'week':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">This Week</h2>
            <p className="text-sm text-gray-500 mb-5">Immediate actions to address no-show patterns</p>

            <div className="text-sm text-gray-700">
              <ul className="space-y-3">
                {[
                  'Review this report and identify your top 3 highest no-show clients',
                  'Schedule a direct conversation with each high-risk client about attendance',
                  'Assess barriers to attendance for each client (transportation, scheduling, motivation)',
                  'Set up or verify reminder system is active for all clients (text + email preferred)'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                    <input type="checkbox" className="mt-1 rounded" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      case 'month':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">This Month</h2>
            <p className="text-sm text-gray-500 mb-5">System-level improvements for attendance</p>

            <div className="text-sm text-gray-700">
              <ul className="space-y-3">
                {[
                  'Implement tiered reminder protocols based on risk level (more touchpoints for high-risk)',
                  'Create an attendance agreement template for clients with chronic no-shows',
                  'Conduct barrier assessment with all moderate-risk clients',
                  'Explore schedule optimization — offer alternative time slots to frequent no-show clients',
                  'Consider implementing a waitlist system to fill last-minute cancellations'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                    <input type="checkbox" className="mt-1 rounded" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      case 'ongoing':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Ongoing</h2>
            <p className="text-sm text-gray-500 mb-5">Continuous monitoring and adjustment</p>

            <div className="text-sm text-gray-700">
              <ul className="space-y-3">
                {[
                  'Re-run this analysis monthly to track improvement and identify new patterns',
                  'Recognize and reinforce clients who improve their attendance',
                  'Watch for early warning signs in currently low-risk clients',
                  'Discuss attendance trends in supervision for team-based insights',
                  'Adjust intervention strategies based on what works for your specific caseload'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                    <input type="checkbox" className="mt-1 rounded" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      case 'success':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">How to Measure Success</h2>
            <p className="text-sm text-gray-500 mb-5">Key metrics and targets for your no-show reduction efforts</p>

            <div className="text-sm text-gray-700">
              <ul className="space-y-3">
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">🎯</span>
                  <div><strong>Target:</strong> Reduce practice no-show rate by 5-10% within 60 days</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">📈</span>
                  <div><strong>Track:</strong> Number of clients moving from high-risk to moderate or low-risk</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">👀</span>
                  <div><strong>Monitor:</strong> Are high-risk clients responding to outreach?</div>
                </li>
                <li className="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <span className="text-lg mt-0.5">🤝</span>
                  <div><strong>Review:</strong> Bring updated data to supervision for collaborative problem-solving</div>
                </li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSubPage(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ATTENDANCE ANALYSIS TAB */}
      {activeTab === 'attendance' && (
        <div>
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Practice Attendance Profile</h2>

          <div className="grid grid-cols-2 gap-6">
            {/* Risk Distribution */}
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-3">Risk Distribution</h3>

              <div className="space-y-3 mb-4">
                {[
                  { label: 'Critical (>50%)', count: criticalCount, pct: totalClients > 0 ? (criticalCount / totalClients * 100).toFixed(1) : 0, color: '#ef4444', bg: 'bg-red-100' },
                  { label: 'High Risk (35-50%)', count: highRiskCount, pct: totalClients > 0 ? (highRiskCount / totalClients * 100).toFixed(1) : 0, color: '#f472b6', bg: 'bg-pink-50' },
                  { label: 'Moderate Risk (25-35%)', count: moderateRiskCount, pct: totalClients > 0 ? (moderateRiskCount / totalClients * 100).toFixed(1) : 0, color: '#f59e0b', bg: 'bg-yellow-50' },
                  { label: 'Low Risk (<25%)', count: lowRiskCount, pct: totalClients > 0 ? (lowRiskCount / totalClients * 100).toFixed(1) : 0, color: '#22c55e', bg: 'bg-green-50' }
                ].map((tier, i) => (
                  <div key={i} className={`${tier.bg} rounded-lg p-3`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-semibold" style={{ color: tier.color }}>{tier.label}</span>
                      <span className="text-sm font-bold">{tier.count} clients ({tier.pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="h-2 rounded-full" style={{ width: `${tier.pct}%`, backgroundColor: tier.color }} />
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-lg font-bold text-blue-900 mb-2 mt-4">Key Metrics</h3>
              <div className="text-sm space-y-1 text-gray-700">
                <p><strong>Total sessions analyzed:</strong> {totalAppointments}</p>
                <p><strong>Total missed appointments:</strong> {totalNoShows}</p>
                <p><strong>Current attendance rate:</strong> {attendanceRate}%</p>
              </div>
            </div>

            {/* Priority Clients */}
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-3">Priority Clients for Outreach</h3>

              {topNoShowClients.length > 0 ? (
                <div className="space-y-3 mb-4">
                  {topNoShowClients.map((client, i) => (
                    <div
                      key={i}
                      className="rounded-lg p-3"
                      style={{
                        borderLeft: `4px solid ${client.noShowPct > 50 ? '#ef4444' : client.noShowPct > 30 ? '#f472b6' : '#f59e0b'}`,
                        backgroundColor: client.noShowPct > 50 ? 'rgba(239,68,68,0.05)' : client.noShowPct > 30 ? 'rgba(244,114,182,0.05)' : 'rgba(245,158,11,0.05)'
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-sm">{client.clientId}</span>
                        <span className="font-bold text-sm" style={{ color: client.noShowPct > 50 ? '#ef4444' : client.noShowPct > 30 ? '#f472b6' : '#f59e0b' }}>
                          {client.noShowPct}% no-show rate
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Missed {client.noShows} of {client.totalAppointments} sessions
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No client data available.</p>
              )}

              <h3 className="text-lg font-bold text-blue-900 mb-2 mt-4">Recommended Approach</h3>
              <div className="text-sm text-gray-700">
                <ol className="list-decimal ml-5 space-y-1">
                  <li><strong>Direct conversation</strong> about attendance patterns</li>
                  <li><strong>Barrier assessment</strong> to identify obstacles</li>
                  <li><strong>Enhanced reminders</strong> via multiple channels</li>
                  <li><strong>Schedule optimization</strong> based on client preference</li>
                  <li><strong>Attendance agreement</strong> with clear expectations</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLIENT INTERVENTIONS TAB */}
      {activeTab === 'interventions' && (
        <div>
          {subPage ? renderInterventionPage() : (
            <>
              <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Client-Specific Intervention Strategies</h2>
              <p className="text-sm text-gray-500 text-center mb-5">Select a category to view detailed strategies</p>

              <div className="space-y-3">
                {INTERVENTION_PAGES.map(item => (
                  <NavCard
                    key={item.id}
                    item={item}
                    count={item.id === 'critical' ? criticalCount : item.id === 'high' ? highRiskCount : item.id === 'moderate' ? moderateRiskCount : item.id === 'low' ? lowRiskCount : undefined}
                    onClick={() => setSubPage(item.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ACTION PLAN TAB */}
      {activeTab === 'action' && (
        <div>
          {subPage ? renderActionPage() : (
            <>
              <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">No-Show Reduction Action Plan</h2>
              <p className="text-sm text-gray-500 text-center mb-5">Select a timeframe to view action items</p>

              <div className="space-y-3">
                {ACTION_PAGES.map(item => (
                  <NavCard
                    key={item.id}
                    item={item}
                    onClick={() => setSubPage(item.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NoShowAnalytics;
